import { useEffect, useRef, useCallback } from 'react';

// Constants
const HEALTH_CHECK_INTERVAL_MS = 60000; // 1 minute
const isDevelopment = process.env.NODE_ENV === 'development';

/**
 * Performance monitoring hook
 * Tracks page load times, component render times, and API call durations
 */
export const usePerformanceMonitoring = () => {
  const componentMountTime = useRef(Date.now());

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // Track page load performance (intentional one-time effect)
    if (window.performance && window.performance.timing) {
      const perfData = window.performance.timing;
      const pageLoadTime = perfData.loadEventEnd - perfData.navigationStart;
      const connectTime = perfData.responseEnd - perfData.requestStart;
      const renderTime = perfData.domComplete - perfData.domLoading;

      if (isDevelopment) {
        console.log('Performance Metrics:', {
          pageLoadTime: `${pageLoadTime}ms`,
          connectTime: `${connectTime}ms`,
          renderTime: `${renderTime}ms`
        });
      }

      // Send to monitoring endpoint
      if (process.env.REACT_APP_BACKEND_URL) {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/performance`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            pageLoadTime,
            connectTime,
            renderTime,
            url: window.location.href,
            timestamp: new Date().toISOString()
          })
        }).catch(err => {
          if (isDevelopment) {
            console.error('Failed to send performance metrics:', err);
          }
        });
      }
    }

    // Track component mount time
    const mountDuration = Date.now() - componentMountTime.current;
    if (isDevelopment) {
      console.log(`Component mounted in ${mountDuration}ms`);
    }

    return () => {
      // Track component unmount
      if (isDevelopment) {
        console.log('Component unmounted');
      }
    };
  }, []);
};

/**
 * API call monitoring wrapper
 * Tracks API call duration and success/failure
 */
export const monitorApiCall = async (apiCall, endpoint) => {
  const startTime = Date.now();
  let success = true;
  let error = null;

  try {
    const result = await apiCall();
    return result;
  } catch (err) {
    success = false;
    error = err.message;
    throw err;
  } finally {
    const duration = Date.now() - startTime;
    
    if (isDevelopment) {
      console.log(`API Call to ${endpoint}:`, {
        duration: `${duration}ms`,
        success,
        error
      });
    }

    // Send to monitoring endpoint
    if (process.env.REACT_APP_BACKEND_URL) {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/api-monitoring`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          endpoint,
          duration,
          success,
          error,
          timestamp: new Date().toISOString()
        })
      }).catch(() => {}); // Silently fail monitoring
    }
  }
};

/**
 * Health check hook
 * Periodically checks backend health
 */
export const useHealthCheck = (intervalMs = HEALTH_CHECK_INTERVAL_MS) => {
  const checkHealth = useCallback(async () => {
    if (!process.env.REACT_APP_BACKEND_URL) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/health`);
      const data = await response.json();
      
      if (isDevelopment) {
        if (data.status === 'healthy') {
          console.log('✓ Backend health check passed', data);
        } else {
          console.warn('⚠ Backend health degraded', data);
        }
      }
    } catch (error) {
      if (isDevelopment) {
        console.error('✗ Backend health check failed:', error);
      }
    }
    // isDevelopment is a constant, process.env is stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    checkHealth();
    const interval = setInterval(checkHealth, intervalMs);
    return () => clearInterval(interval);
  }, [checkHealth, intervalMs]);
};

/**
 * User activity tracking
 */
export const trackUserActivity = (eventName, eventData = {}) => {
  console.log(`User Activity: ${eventName}`, eventData);

  // Send to monitoring endpoint
  if (process.env.REACT_APP_BACKEND_URL) {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/track-activity`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        eventName,
        eventData,
        url: window.location.href,
        timestamp: new Date().toISOString()
      })
    }).catch(err => console.error('Failed to track activity:', err));
  }

  // Google Analytics (if available)
  if (window.gtag) {
    window.gtag('event', eventName, eventData);
  }
};
